import Button from '../shared/Button.js';
// ============================================================================
// src/pig/PigsHomeView.tsx  —  Phase 2 Round 6
// ----------------------------------------------------------------------------
// Pigs home dashboard. It used to read every one of its five inputs through
// App-owned providers — four app_store blobs off usePig() and the pig daily
// history off useDailysRecent() — and owned no read of its own. `pig.home.core`
// owns all five now, which is what lets `pigsHome` retire from the frozen
// app_store and pig_dailys consumer lists in
// tests/fixtures/former-eager-dependencies.json.
//
// This surface writes NOTHING. It has no props, no setter, and no mutation, so
// unlike /pig/breeding or /pig/sows there is no App-owned write left behind it
// and the declaration's residual list is genuinely empty. Batch cards navigate
// directly to their existing record route (A32).
// ============================================================================
import React from 'react';
import {openableProps} from '../shared/openable.js';
import {fmt, fmtS, todayISO, addDays} from '../lib/dateUtils.js';

import {calcBreedingTimeline, calcCycleStatus, buildCycleSeqMap, PIG_GROUP_COLORS, PIG_GROUPS} from '../lib/pig.js';
import type {
  PigBreederRow,
  PigBreedingCycle,
  PigBreedingTimeline,
  PigFarrowingRecord,
  PigFeederGroup,
  PigProcessingTrip,
} from '../lib/pig.js';
import {StatStrip} from '../shared/StatStrip.js';
import {GroupDot, groupColor} from '../shared/GroupPillPicker.js';
import {
  PROCESSING_STATUS_DISPLAY,
  pigBatchProcessingStatusLabel,
  pigBatchProcessingStatusVariant,
} from '../lib/processingStatusDisplay.js';
import Badge from '../shared/Badge.jsx';
import {useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {PigHomeCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';
import {farrowingSurvivalTone, type ValueTone} from '../lib/pigThresholds.js';
import {isSowsDailyLabel, latestSowsDailyCount} from '../lib/pigSowGroups.js';

// ── Row shapes ──────────────────────────────────────────────────────────────
// pig.home.core hands the four record arrays back as open row maps — they are
// app_store blobs (ppp-feeders-v1, ppp-breeding-v1, ppp-farrowing-v1,
// ppp-breeders-v1) and nothing between the select and this file reads a field
// off them, exactly as PigContext did not. src/lib/pig.ts already declares the
// shapes, and they are the same declarations calcBreedingTimeline,
// buildCycleSeqMap and pigBatchProcessingStatusLabel already take as arguments,
// so this dashboard narrows to those rather than re-describing the same rows.
//
// Only the fields those canonical types leave to their index signature are
// added below, and only where this file actually reads them.

// A processing trip's date. PigProcessingTrip names pigCount / liveWeights /
// hangingWeight / subAttributions and leaves the rest to `[field: string]:
// unknown`; the yield trend and the year filter read `date` as the ISO string
// PigBatchesView's trip form writes.
type PigsHomeTrip = PigProcessingTrip & {date?: string | null};

// The pig_dailys columns this dashboard reads. pig.home.core hands the rows
// back as `select('*')` produced them — the same loader App's boot used — so
// nothing in TypeScript describes them and these are what is read here.
// `date` is not optional: every sort compares it with localeCompare unguarded,
// exactly as before. The numeric columns keep the `number | string` union the
// sibling declaration for this table uses (src/pig/PigDailysView.tsx), because
// the same rows are round-tripped through the edit form there.
type PigsHomeDailyRow = {
  date: string;
  batch_label?: string | null;
  pig_count?: number | string | null;
  feed_lbs?: number | string | null;
};

// The shapes the three `.map(...).filter(Boolean)` chains below produce.
// `.filter(Boolean)` does not remove `null` from an element type for
// TypeScript, so each chain asserts its surviving element type. The assertion
// is what keeps the filter itself byte-identical — rewriting it as a type
// predicate would change live code for a purely static reason.
type UpcomingFarrowing = {group: PigBreedingCycle['group']; start: string; end: string};
type CycleSurvival = {label: string; survival: number; born: number; dead: number};

// The locally declared stat tile accepts rendered children and a color token.
// `date` is not optional here even though PigsHomeTrip's is: the sort below
// calls `a.date.localeCompare(...)` unguarded, so a trip with no date already
// threw before this conversion and still would.
type YieldPoint = {date: string; yield: number; batch: string | null | undefined};

// A cycle that produced a timeline. calcBreedingTimeline returns null without
// an exposureStart, so anything surviving the filter has one — which is why the
// breeding-group sort compares `c.exposureStart` unguarded, as it always has.
type CycleWithTimeline = {c: PigBreedingCycle & {exposureStart: string}; tl: PigBreedingTimeline};

// PIG_GROUPS holds the group ids as STRINGS ('1','2','3'); PIG_GROUP_COLORS, in
// the same module, is keyed by the NUMERIC literals 1|2|3. JavaScript property
// access coerces the two and this lookup has always relied on that. Both
// declarations live in src/lib/pig.ts, outside this cluster, so the coercion is
// recorded here as a string-keyed view of the same object rather than either
// declaration being changed.
type PigGroupColorTable = Record<string, (typeof PIG_GROUP_COLORS)[keyof typeof PIG_GROUP_COLORS]>;

// What the breeding-group panel's map returns. The "no cycles" early return
// carries only four of these keys and the full return carries eight, so the
// callback is annotated with the union of both rather than letting TypeScript
// infer two incompatible object types — the render reads `nextStep`,
// `nextDate`, `daysToNext` and `isActive` off every element and gets
// `undefined` for the short shape, exactly as it does today.
type GroupStatus = {
  group: string;
  phase: string;
  next?: null;
  nextStep?: string;
  nextDate?: string | null;
  daysToNext?: number | null;
  isActive?: boolean;
  color: (typeof PIG_GROUP_COLORS)[keyof typeof PIG_GROUP_COLORS];
  tl?: PigBreedingTimeline;
  cycle?: PigBreedingCycle;
};

const PIG_HOME_PARAMS = Object.freeze({});

// R08: a card is radius 18 on --shadow-card; a section title above a group of
// cards is 18/800, the same heading the other program dashboards use.
const cardS: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-card)',
  boxShadow: 'var(--shadow-card)',
};
const sectionTitleS: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: '-.01em',
  color: 'var(--ink)',
};

export default function PigsHomeView() {
  const navigate = useNavigate();
  const homeResource = useRouteResource(RESOURCE.PIG_HOME_CORE, PIG_HOME_PARAMS);
  const homeData = homeResource.data as PigHomeCoreData | null;
  // `useBatches().batches` USED TO BE DESTRUCTURED HERE AND IS GONE RATHER THAN
  // MERELY MOVED. It was never read: the broiler batch array reached this file
  // and no expression consumed it, so localizing it would have been inventing a
  // dependency instead of relocating one. Dropping it is part of the same
  // change because `batches` is one of the app_store fields the former-eager
  // derivation scores, and a surface cannot retire from that list while a
  // provider hand-off it does not use keeps it on the books.
  //
  // The four blob arrays and the daily history are the same values App's boot
  // published; they are asserted to the same shapes the previous destructure
  // asserted, at the same single point.
  const breeders = (homeData?.breeders || []) as PigBreederRow[];
  const feederGroups = (homeData?.feederGroups || []) as PigFeederGroup[];
  const farrowingRecs = (homeData?.farrowingRecs || []) as PigFarrowingRecord[];
  const breedingCycles = (homeData?.breedingCycles || []) as PigBreedingCycle[];
  const pigDailys = (homeData?.pigDailys || []) as PigsHomeDailyRow[];

  // THREE STATES WHERE THERE USED TO BE ONE, AND THIS PAGE HAS TO RETURN EARLY
  // FOR TWO OF THEM.
  //
  // The dashboard arrived with the boot payload, so "empty" and "not loaded
  // yet" were the same frame and "unreachable" could not happen. A route-local
  // read makes all three real — and every tile below is a reduction over these
  // five arrays, so painting the page while they are empty renders "0 pigs on
  // farm, 0 active sows, — survival" over a farm that has all three. That is
  // the empty-farm lie this migration exists to remove, not to introduce, and
  // it cannot be fixed tile by tile the way a list's empty state can.
  //
  // Placed after every hook this component calls (there are exactly two, both
  // above) so the early return cannot change hook order.
  //
  // `Boolean()`, not a bare truthiness chain: `error` is `unknown` on the
  // resource contract, so the && expression would be `unknown` and could not be
  // a ReactNode.
  if (homeResource.loading || Boolean(homeResource.error)) {
    return (
      <div style={{minHeight: '100vh', background: 'var(--bg-page)'}} data-pig-home-loaded="false">
        <Header />
        <div style={{padding: '1.25rem', maxWidth: 1180, margin: '0 auto'}}>
          {homeResource.loading ? (
            <div
              data-pig-home-loading
              style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}
            >
              Loading pig dashboard{'…'}
            </div>
          ) : (
            <div
              data-pig-home-load-error
              style={{textAlign: 'center', padding: '3rem', color: 'var(--danger-ink)', fontSize: 13}}
            >
              Could not load the pig dashboard.{' '}
              <Button type="button" onClick={() => homeResource.refetch()}>
                Retry
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Both call sites pass farrowingRecs, so the parameter is named rather than
  // generic. The filtered array is asserted to number[]: `x != null &&
  // !isNaN(x)` is a real numeric filter that TypeScript's filter signature
  // cannot carry through, and the assertion leaves the call byte-identical.
  const avg = (arr: readonly PigFarrowingRecord[], fn: (record: PigFarrowingRecord) => number | null) => {
    const v = arr.map(fn).filter((x) => x != null && !isNaN(x)) as number[];
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };
  const EMPTY_METRIC_LABEL = 'No data yet';

  const activeFeeders = feederGroups.filter((g) => g.status === 'active');

  const todayStr = todayISO();

  // Sow performance across all farrowing records
  const totalBorn = farrowingRecs.reduce((s, r) => s + (parseInt(r.totalBorn as string) || 0), 0);
  const totalDeaths = farrowingRecs.reduce((s, r) => s + (parseInt(r.deaths as string) || 0), 0);
  const survivalRate = totalBorn > 0 ? Math.round(((totalBorn - totalDeaths) / totalBorn) * 100) : null;

  const totalFarrowed = farrowingRecs.length;
  // Active sows/boars from breeder registry
  const activeSows = breeders.filter((b) => !b.archived && (b.sex === 'Sow' || b.sex === 'Gilt')).length;
  const activeBoars = breeders.filter((b) => !b.archived && b.sex === 'Boar').length;
  // Total pigs on farm — only active batches + breeding stock
  const activeFeederNames = activeFeeders.flatMap((g) => {
    const subs = (g.subBatches || []).filter((s) => s.status === 'active');
    return subs.length > 0
      ? subs.map((s) => (s.name || '').toLowerCase().trim())
      : [(g.batchName || '').toLowerCase().trim()];
  });
  const pigCountsByGroup = (() => {
    // `d.pig_count > 0` is a relational compare on a column typed `number |
    // string`; JavaScript coerces both sides to numbers here, so the assertion
    // records what the comparison already does rather than changing it.
    const counts: Record<string, number> = {};
    [...pigDailys]
      .sort((a, b) => a.date.localeCompare(b.date))
      .forEach((d) => {
        if ((d.pig_count as number) > 0 && d.batch_label) {
          const lbl = d.batch_label.toLowerCase().trim();
          if (isSowsDailyLabel(lbl)) return; // folded into one SOWS entry below
          if (activeFeederNames.includes(lbl) || lbl === 'boars')
            counts[d.batch_label] = parseInt(d.pig_count as string);
        }
      });
    // SOWS: the three sow-group rows on the latest date summed, or a legacy
    // combined 'SOWS' row (owner request 2026-11-04 split SOWS into groups).
    const sows = latestSowsDailyCount(pigDailys);
    if (sows) counts.SOWS = sows.count;
    return counts;
  })();
  const pigsOnFarm = Object.values(pigCountsByGroup).reduce((s, v) => s + v, 0);
  const pigBreakdown = Object.entries(pigCountsByGroup)
    .sort((a, b) => b[1] - a[1])
    .map((e) => e[0] + ': ' + e[1])
    .join(' \u00b7 ');
  // Pigs processed + avg yield from trips
  const allTrips = feederGroups.flatMap((g) =>
    ((g.processingTrips || []) as PigsHomeTrip[]).map((t) => ({...t, batch: g.batchName})),
  );
  const currentYear = new Date().getFullYear().toString();
  const yearTrips = allTrips.filter((t) => (t.date || '').startsWith(currentYear));

  // Next farrowing window
  const nextFarrowing = (() => {
    const today2 = todayISO();
    const upcoming = (
      breedingCycles
        .map((c) => {
          const tl = calcBreedingTimeline(c.exposureStart);
          if (!tl) return null;
          if (tl.farrowingEnd < today2) return null;
          return {group: c.group, start: tl.farrowingStart, end: tl.farrowingEnd};
        })
        .filter(Boolean) as UpcomingFarrowing[]
    ).sort((a, b) => a.start.localeCompare(b.start));
    return upcoming.length > 0 ? upcoming[0] : null;
  })();

  // Recent 5 cycles survival
  const _pigsHomeSeqMap = buildCycleSeqMap(breedingCycles);
  const recentCycleSurvival = breedingCycles
    .slice(-5)
    .map((c) => {
      const tl = calcBreedingTimeline(c.exposureStart);
      if (!tl) return null;
      const recs = farrowingRecs.filter((r) => {
        if (r.group !== c.group || !r.farrowingDate) return false;
        const rd = new Date(r.farrowingDate + 'T12:00:00');
        return (
          rd >= new Date(tl.farrowingStart + 'T12:00:00') && rd <= addDays(new Date(tl.farrowingEnd + 'T12:00:00'), 14)
        );
      });
      if (!recs.length) return null;
      const born = recs.reduce((s, r) => s + (parseInt(r.totalBorn as string) || 0), 0);
      const dead = recs.reduce((s, r) => s + (parseInt(r.deaths as string) || 0), 0);
      const _suf = _pigsHomeSeqMap[c.id as string];
      return {
        label: `G${c.group}${_suf ? ' · ' + _suf : ''} ${fmtS(c.exposureStart)}`,
        survival: born > 0 ? Math.round(((born - dead) / born) * 100) : 0,
        born,
        dead,
      };
    })
    .filter(Boolean) as CycleSurvival[];

  // Feeder batch performance
  const feederBatchStats = activeFeeders.map((g) => {
    const activeSubs = (g.subBatches || []).filter((s) => s.status === 'active');
    const subNames =
      activeSubs.length > 0
        ? activeSubs.map((s) => (s.name || '').toLowerCase().trim())
        : [(g.batchName as string).toLowerCase().trim()];
    const latestCounts: Record<string, number> = {};
    [...pigDailys]
      .sort((a, b) => a.date.localeCompare(b.date))
      .forEach((d) => {
        if ((d.pig_count as number) > 0 && d.batch_label && subNames.includes(d.batch_label.toLowerCase().trim()))
          latestCounts[d.batch_label] = parseInt(d.pig_count as string);
      });
    const currentCount = Object.values(latestCounts).reduce((s, v) => s + v, 0);
    const batchDailys = pigDailys.filter((d) => {
      if (activeSubs.length > 0)
        return activeSubs.some(
          (s) => (d.batch_label || '').toLowerCase().trim() === (s.name || '').toLowerCase().trim(),
        );
      return (d.batch_label || '').toLowerCase().trim() === (g.batchName || '').toLowerCase().trim();
    });
    // Raw feed minus what's been allocated out to breeding-transferred pigs
    // (see PigBatchesView transfer flow).
    const rawFeed = batchDailys.reduce((s, d) => s + (parseFloat(d.feed_lbs as string) || 0), 0);
    const totalFeed = Math.max(0, rawFeed - (parseFloat(g.feedAllocatedToTransfers as string) || 0));
    const originalCount = parseInt(g.originalPigCount as string) || 0;
    const reportDays = new Set(batchDailys.map((d) => d.date)).size;
    const cycle = breedingCycles.find((c) => c.id === g.cycleId);
    const tl = cycle ? calcBreedingTimeline(cycle.exposureStart) : null;
    // Use the OLDEST actual farrow date (to match the batch tile's age
    // range upper bound). Falls back to the theoretical farrowingStart
    // when no actual farrowing records exist for this cycle yet.
    let ageFromDate: string | null | undefined = null;
    if (cycle && tl) {
      const recs = farrowingRecs.filter((r) => {
        if (r.group !== cycle.group || !r.farrowingDate) return false;
        const rd = new Date(r.farrowingDate + 'T12:00:00');
        return (
          rd >= new Date(tl.farrowingStart + 'T12:00:00') && rd <= addDays(new Date(tl.farrowingEnd + 'T12:00:00'), 14)
        );
      });
      ageFromDate = recs.length > 0 ? recs.map((r) => r.farrowingDate).sort()[0] : tl.farrowingStart;
    }
    // `dateA - dateB` coerces both operands through Date.prototype.valueOf,
    // which IS getTime(); spelling it out is what TypeScript's arithmetic rule
    // requires and produces the same number.
    const daysOld = ageFromDate
      ? Math.round((new Date().getTime() - new Date(ageFromDate + 'T12:00:00').getTime()) / 86400000)
      : null;
    const perLbCost = parseFloat(g.perLbFeedCost as string) || 0;
    const totalFeedCost = totalFeed > 0 && perLbCost > 0 ? totalFeed * perLbCost : null;
    const costPerPig = totalFeedCost != null && originalCount > 0 ? totalFeedCost / originalCount : null;
    const statusMetrics = {started: originalCount, current: currentCount};
    return {
      g,
      currentCount,
      statusLabel: pigBatchProcessingStatusLabel(g, statusMetrics),
      statusVariant: pigBatchProcessingStatusVariant(g, statusMetrics),
      totalFeed,
      originalCount,
      reportDays,
      daysOld,
      feedPerPig: originalCount > 0 && totalFeed > 0 ? (totalFeed / originalCount).toFixed(0) : null,
      totalFeedCost,
      costPerPig,
    };
  });
  const inProcessFeederBatchCount = feederBatchStats.filter(
    (stat) => stat.statusLabel === PROCESSING_STATUS_DISPLAY.inProcess,
  ).length;

  // Carcass yield trend from processing trips
  const yieldTrend = (
    feederGroups
      .flatMap((g) =>
        ((g.processingTrips || []) as PigsHomeTrip[]).map((t) => {
          const live = (t.liveWeights || '')
            .split(/[\s,]+/)
            .map((v) => parseFloat(v))
            .filter((v) => !isNaN(v) && v > 0)
            .reduce((a, b) => a + b, 0);
          const hang = parseFloat(t.hangingWeight as string) || 0;
          return live > 0 && hang > 0
            ? {date: t.date, yield: Math.round((hang / live) * 1000) / 10, batch: g.batchName}
            : null;
        }),
      )
      .filter(Boolean) as YieldPoint[]
  )
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-10);

  const recentPigDailys = [...pigDailys].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);

  const trendBar = (val: number, max: number, color: string, tone: ValueTone | null = null) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4}}>
      <div
        style={{
          flex: 1,
          height: 7,
          background: 'var(--surface-2)',
          borderRadius: 4 /* radius-allow: trend bar track (height 7) */,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${Math.min(100, max > 0 ? (val / max) * 100 : 0)}%`,
            background: color,
            borderRadius: 4 /* radius-allow: trend bar fill (height 7) */,
          }}
        />
      </div>
      <span
        style={{fontSize: 10, color: tone ? tone.ink : 'var(--ink)', fontWeight: 600, minWidth: 36, textAlign: 'right'}}
      >
        {Math.round(val * 10) / 10}
      </span>
      {tone && <span style={{fontSize: 10, color: tone.ink, fontWeight: 600}}>{tone.word}</span>}
    </div>
  );

  // The boot-release readiness marker. This return and the early return above
  // are mutually exclusive, so the attribute is a literal per branch rather
  // than the single `loading || loadError` expression CattleHomeView and
  // SheepHomeView use. `"true"` means pig.home.core resolved and every tile
  // below is reducing over real rows rather than the five empty arrays.
  return (
    <div style={{minHeight: '100vh', background: 'var(--bg-page)'}} data-pig-home-loaded="true">
      <Header />
      <div
        style={{
          padding: '1.25rem',
          maxWidth: 1180,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}
      >
        {/* N09 page H1. R11: the descriptive sub-line under it is dropped. */}
        <h1 className="wcf-page-title">Pig Dashboard</h1>
        {/* R15: one joined stat strip, not four loose tiles. The values are the
            same four expressions; the two sub-lines are data (R11). Survival's
            80/65 cut-offs now take the warn / danger soft tint (R03) instead
            of colored ink. data-mobile-2col keeps the phone 2-up grid. */}
        <StatStrip
          rootAttrs={{'data-mobile-2col': '1', 'data-pig-home-strip': '1'}}
          tiles={[
            {label: 'Pigs on Farm', value: pigsOnFarm > 0 ? pigsOnFarm.toLocaleString() : null},
            {label: 'Active Sows', value: activeSows, sub: activeBoars + ' boar' + (activeBoars !== 1 ? 's' : '')},
            {label: 'In Process Batches', value: inProcessFeederBatchCount},
            {
              label: 'Overall Survival',
              value: survivalRate != null ? survivalRate + '%' : null,
              tone: survivalRate != null && survivalRate < 80 ? (survivalRate >= 65 ? 'warn' : 'danger') : undefined,
              sub: totalFarrowed + ' records',
            },
          ]}
        />
        {pigsOnFarm > 0 && (
          <div>
            <h2 style={{...sectionTitleS, marginBottom: 10}}>Pigs on Farm Breakdown</h2>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 8}}>
              {Object.entries(pigCountsByGroup)
                .sort((a, b) => {
                  const al = a[0].toLowerCase(),
                    bl = b[0].toLowerCase();
                  if (al === 'sows') return -1;
                  if (bl === 'sows') return 1;
                  if (al === 'boars') return -1;
                  if (bl === 'boars') return 1;
                  return al.localeCompare(bl);
                })
                .map(([name, count]) => (
                  <div
                    key={name}
                    style={{
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--r-chip)',
                      padding: '10px 14px',
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--ink-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        marginBottom: 3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {name}
                    </div>
                    <div style={{fontSize: 20, fontWeight: 700, color: 'var(--text-primary)'}}>{count}</div>
                  </div>
                ))}
            </div>
          </div>
        )}
        {/* Breeding group status — current phase + next milestone for each group */}
        {(function () {
          var today3 = todayISO();
          var groupStatuses = PIG_GROUPS.map(function (g): GroupStatus {
            // Find the most recent active or upcoming cycle for this group
            var cycles = (
              breedingCycles
                .filter(function (c) {
                  return c.group === g;
                })
                .map(function (c) {
                  var tl = calcBreedingTimeline(c.exposureStart);
                  if (!tl) return null;
                  return {c: c, tl: tl};
                })
                .filter(Boolean) as CycleWithTimeline[]
            ).sort(function (a, b) {
              return a.c.exposureStart.localeCompare(b.c.exposureStart);
            });
            // Find the cycle that's currently active or the next upcoming one
            var current: CycleWithTimeline | null = null;
            for (var ci = cycles.length - 1; ci >= 0; ci--) {
              var ct = cycles[ci];
              if (ct.tl.boarStart <= today3 && ct.tl.growEnd >= today3) {
                current = ct;
                break;
              }
            }
            if (!current) {
              // No active cycle — find the next upcoming one
              for (var ci2 = 0; ci2 < cycles.length; ci2++) {
                if (cycles[ci2].tl.boarStart > today3) {
                  current = cycles[ci2];
                  break;
                }
              }
            }
            if (!current && cycles.length > 0) current = cycles[cycles.length - 1]; // fallback to most recent
            if (!current)
              return {group: g, phase: 'No cycles', next: null, color: (PIG_GROUP_COLORS as PigGroupColorTable)[g]};

            var tl = current.tl;
            var phase = '',
              nextStep = '',
              nextDate: string | null = '',
              isActive = false;
            if (today3 < tl.boarStart) {
              phase = 'Planned';
              nextStep = 'Boar Exposure starts';
              nextDate = tl.boarStart;
            } else if (today3 <= tl.boarEnd) {
              phase = 'Boar Exposure';
              nextStep = 'Exposure Paddock';
              nextDate = tl.paddockStart;
              isActive = true;
            } else if (today3 <= tl.paddockEnd) {
              phase = 'Exp. Paddock';
              nextStep = 'Farrowing window opens';
              nextDate = tl.farrowingStart;
              isActive = true;
            } else if (today3 <= tl.farrowingEnd) {
              phase = 'Farrowing';
              nextStep = 'Weaning starts';
              nextDate = tl.weaningStart;
              isActive = true;
            } else if (today3 <= tl.weaningEnd) {
              phase = 'Weaning';
              nextStep = 'Grow-out starts';
              nextDate = tl.growStart;
              isActive = true;
            } else if (today3 <= tl.growEnd) {
              phase = 'Grow-out';
              nextStep = 'Cycle complete';
              nextDate = tl.growEnd;
              isActive = true;
            } else {
              phase = 'Completed';
              nextStep = 'Start next cycle';
              nextDate = null;
            }
            var daysToNext = nextDate
              ? Math.max(
                  0,
                  Math.round(
                    (new Date(nextDate + 'T12:00:00').getTime() - new Date(today3 + 'T12:00:00').getTime()) / 86400000,
                  ),
                )
              : null;
            return {
              group: g,
              phase: phase,
              nextStep: nextStep,
              nextDate: nextDate,
              daysToNext: daysToNext,
              isActive: isActive,
              color: (PIG_GROUP_COLORS as PigGroupColorTable)[g],
              tl: tl,
              cycle: current.c,
            };
          });
          if (
            groupStatuses.every(function (gs) {
              return gs.phase === 'No cycles';
            })
          )
            return null;
          return React.createElement(
            'div',
            null,
            React.createElement('h2', {style: {...sectionTitleS, marginBottom: 10}}, 'Breeding Group Status'),
            React.createElement(
              'div',
              {style: {display: 'grid', gridTemplateColumns: 'repeat(' + PIG_GROUPS.length + ',1fr)', gap: 10}},
              groupStatuses.map(function (gs) {
                return React.createElement(
                  'div',
                  {
                    key: gs.group,
                    // R06: the group's own palette color by the pig convention
                    // the Gantt and breeding lists use (PIG_GROUPS order into
                    // --group-1..6), as a 6% tint on its card; R08 shell.
                    style: {
                      ...cardS,
                      background:
                        'color-mix(in srgb, ' + groupColor(PIG_GROUPS.indexOf(gs.group)) + ' 6%, var(--surface))',
                      overflow: 'hidden',
                    },
                  },
                  React.createElement(
                    'div',
                    {
                      style: {
                        borderBottom: '1px solid var(--hairline)',
                        padding: '8px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      },
                    },
                    React.createElement(GroupDot, {color: groupColor(PIG_GROUPS.indexOf(gs.group))}),
                    React.createElement(
                      'span',
                      {style: {fontSize: 14, fontWeight: 800, color: 'var(--ink)'}},
                      'Group ' + gs.group,
                    ),
                  ),
                  React.createElement(
                    'div',
                    {style: {padding: '10px 14px'}},
                    React.createElement(
                      'div',
                      {style: {display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6}},
                      React.createElement(Badge, {status: gs.isActive ? 'active' : 'planned'}, gs.phase),
                    ),
                    gs.nextStep &&
                      React.createElement(
                        'div',
                        {style: {fontSize: 12, color: 'var(--ink)', marginBottom: 2}},
                        React.createElement('span', {style: {color: 'var(--ink-faint)'}}, 'Next: '),
                        React.createElement('span', {style: {fontWeight: 600}}, gs.nextStep),
                      ),
                    gs.nextDate &&
                      React.createElement(
                        'div',
                        {style: {fontSize: 11, color: 'var(--ink-muted)'}},
                        fmt(gs.nextDate),
                        gs.daysToNext != null &&
                          gs.daysToNext > 0 &&
                          React.createElement(
                            'span',
                            {
                              style: {
                                marginLeft: 6,
                                fontWeight: 600,
                                color: gs.daysToNext <= 7 ? 'var(--danger-ink)' : 'var(--text-secondary)',
                              },
                            },
                            '(' + gs.daysToNext + ' days)',
                          ),
                      ),
                  ),
                );
              }),
            ),
          );
        })()}

        {/* Active feeder batches */}
        {feederBatchStats.length > 0 && (
          <div>
            <h2 style={{...sectionTitleS, marginBottom: 10}}>Feeder Batches</h2>
            <div style={{display: 'flex', flexDirection: 'column', gap: 14}}>
              {feederBatchStats.map(
                ({
                  g,
                  currentCount,
                  statusLabel,
                  statusVariant,
                  totalFeed,
                  originalCount,
                  reportDays,
                  daysOld,
                  feedPerPig,
                  totalFeedCost,
                  costPerPig,
                }) => {
                  const hasFeederDashboardData =
                    currentCount > 0 ||
                    originalCount > 0 ||
                    totalFeed > 0 ||
                    reportDays > 0 ||
                    totalFeedCost != null ||
                    costPerPig != null;
                  return (
                    <div
                      key={g.id}
                      {...(g.id
                        ? openableProps(() => {
                            if (g.id) navigate('/pig/batches/' + encodeURIComponent(g.id));
                          })
                        : {})}
                      style={{...cardS, overflow: 'hidden', cursor: 'pointer'}}
                      className="wcf-clickable"
                    >
                      <div
                        style={{
                          borderBottom: '1px solid var(--hairline)',
                          padding: '10px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{fontSize: 16, fontWeight: 800, color: 'var(--ink)'}}>{g.batchName}</span>
                        <Badge status={statusLabel}>{statusLabel}</Badge>
                        {daysOld != null && (
                          <span style={{fontSize: 11, color: 'var(--text-secondary)'}}>
                            {Math.floor(daysOld / 30) + 'm ' + Math.floor((daysOld % 30) / 7) + 'w old'}
                          </span>
                        )}
                      </div>
                      <div style={{padding: '12px 18px'}}>
                        <p style={{color: 'var(--text-2)', margin: '0 0 10px'}}>Of {originalCount} started</p>
                        {!hasFeederDashboardData ? (
                          <div
                            data-pig-dashboard-empty="feeder-batch"
                            style={{
                              padding: '10px 12px',
                              background: 'var(--surface-2)',
                              border: '1px solid var(--divider)',
                              borderRadius: 10,
                              color: 'var(--ink-muted)',
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            {EMPTY_METRIC_LABEL}
                          </div>
                        ) : (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))',
                              gap: 8,
                            }}
                          >
                            {[
                              {l: 'Current', v: currentCount > 0 ? currentCount.toString() : '\u2014'},

                              {
                                l: 'Total Feed',
                                v: totalFeed > 0 ? Math.round(totalFeed).toLocaleString() + ' lbs' : '\u2014',
                              },
                              {l: 'Feed / Pig', v: feedPerPig ? feedPerPig + ' lbs' : '\u2014'},

                              {l: 'Report Days', v: reportDays.toString()},
                            ].map((t) => (
                              <div
                                key={t.l}
                                style={{
                                  padding: '8px 10px',
                                  background: 'var(--surface-2)',
                                  border: '1px solid var(--divider)',
                                  borderRadius: 10,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 9,
                                    color: 'var(--ink-faint)',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.4,
                                    marginBottom: 2,
                                  }}
                                >
                                  {t.l}
                                </div>
                                <div style={{fontSize: 14, fontWeight: 700, color: 'var(--text-primary)'}}>{t.v}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </div>
        )}

        {/* Farrowing survival + Carcass yield side by side */}
        {(recentCycleSurvival.length >= 2 || yieldTrend.length >= 2) && (
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14}}>
            {recentCycleSurvival.length >= 2 && (
              <div>
                <h2 style={{...sectionTitleS, marginBottom: 10}}>Farrowing Survival</h2>
                <div style={{...cardS, padding: '14px 16px'}}>
                  {recentCycleSurvival.map((c, i) => (
                    <div key={i}>
                      <div style={{fontSize: 10, color: 'var(--ink-muted)', marginBottom: 1}}>
                        {c.label} {'\u2014'} {c.born} born, {c.dead} died
                      </div>
                      {/* D02/\u00a74 (E4 residual): per-item comparison bars are
                          neutral; the survival number carries the read. F15:
                          Table D's 80/65 cut-offs tone the VALUE, with the word
                          'Low Survival' — never the bar fill. */}
                      {trendBar(c.survival, 100, 'var(--text-secondary)', farrowingSurvivalTone(c.survival))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {yieldTrend.length >= 2 && (
              <div>
                <h2 style={{...sectionTitleS, marginBottom: 10}}>Carcass Yield Trend</h2>
                <div style={{...cardS, padding: '14px 16px'}}>
                  {yieldTrend.map((t, i) => (
                    <div key={i}>
                      <div style={{fontSize: 10, color: 'var(--ink-muted)', marginBottom: 1}}>
                        {t.batch} {'\u00b7'} {fmt(t.date)}
                      </div>
                      {trendBar(t.yield, 100, 'var(--text-secondary)')}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
